import {t} from "@lingui/core/macro";
import moment from "moment";

import {RequestInfo} from "@repo/shared-core";

export const DATE_FORMAT = "YYYY-MM-DD";
// to be used when ignoring timezone offsets
export const DATETIME_FORMAT = "YYYY-MM-DDTHH:mm:ss[Z]";

export type DateRange = {
    startDate: moment.Moment;
    endDate: moment.Moment;
};

/**
 * Converts a date string in either "YYYY-MM-DD" or "YYYY-MM-DDTHH:mm:ss[Z]" format
 * to a moment object while discarding any time component.
 *
 * This ensures that the date is treated as a naive date, without a timezone offset.
 * For example, "2024-01-01T00:00:00Z" and "2024-01-01" will both be interpreted
 * as January 1st, 2024, without considering time zones.
 *
 * @param {string} dateString - The date string to parse.
 * @returns {moment.Moment} A moment object representing the date.
 * @throws {Error} If the date string is not in a valid format.
 */
export const dateStringToMoment = (dateString: string): moment.Moment => {
    if (!/^\d{4}-\d{2}-\d{2}(T\d{2}:\d{2}:\d{2}(Z)?)?$/.test(dateString)) {
        throw new Error(
            `Invalid date string: "${dateString}". Expected format: YYYY-MM-DD or YYYY-MM-DDTHH:mm:ss[Z]`,
        );
    }

    // Extract only the date part if a full datetime is provided
    const datePart = dateString.split("T")[0];

    return moment(datePart, DATE_FORMAT, true);
};

/**
 * Converts a Moment.js object to a formatted date string using the specified format.
 * Ensures that the output uses Western Arabic numerals (0-9) by setting the locale to English.
 *
 * This is used when converting a moment to a string for "logic" purposes,
 * such as when generating a key for local storage, or when converting a date
 * to a string for a URL param or a graphQL variable, which should all use
 * English numerals regardless of the locale of the user.
 *
 * Typically used with DATE_FORMAT or DATETIME_FORMAT.
 *
 * @param {moment.Moment} moment - The Moment.js object to format.
 * @param {string} format - The desired date format string (e.g., "YYYY-MM-DD").
 * @returns {string} The formatted date string in the specified format.
 *
 * @example
 * const date = moment("2025-02-23");
 * console.log(momentToDateString(date, "YYYY-MM-DD")); // "2025-02-23"
 */
export const momentToNonDisplayString = (
    moment: moment.Moment | null | undefined,
    format: string,
) => {
    if (!moment) {
        return undefined;
    }
    return moment.clone().locale("en").format(format);
};

/** Compute the percent value of this amount & total. Use for sorting. */
export function computePercent(amount: number, total: number): number {
    // When there's no total, there's no clearly correct percentage value. But
    // for our district report use cases, it feels a bit more natural to
    // represent this as "nothing done" rather than "everything done" or
    // "indeterminate", so we return 0.
    if (total === 0) {
        return 0;
    }

    return (amount / total) * 100;
}

/** Render the percent value of this amount & total. Use for display. */
export function renderPercent(amount: number, total: number): string {
    return t`${Math.round(computePercent(amount, total))}%`;
}

export function roundPercentage(percentage: number): number {
    return Math.round(percentage * 100) / 100;
}

/** Render a boolean as "Yes" or "No". */
export function renderYesOrNo(trueOrFalse: boolean): string {
    return trueOrFalse ? t`Yes` : t`No`;
}

export function getDateRangeLabel(
    fromDate: moment.Moment,
    upToDate: moment.Moment,
): string {
    const dateOptions = {
        month: "short",
        day: "numeric",
    } as const;
    const kaLocale = RequestInfo.kaLocale;

    if (fromDate.isSame(upToDate, "day")) {
        return `${fromDate.toDate().toLocaleDateString(kaLocale, dateOptions)}`;
    }

    if (fromDate.isSame(upToDate, "month")) {
        return `${fromDate
            .toDate()
            .toLocaleDateString(kaLocale, dateOptions)} - ${upToDate
            .toDate()
            .toLocaleDateString(kaLocale, {
                day: "numeric",
            })}`;
    }

    return `${fromDate
        .toDate()
        .toLocaleDateString(
            kaLocale,
            dateOptions,
        )} - ${upToDate.toDate().toLocaleDateString(kaLocale, dateOptions)}`;
}

export function getDateRangeLabelWithYear(
    fromDate: moment.Moment,
    upToDate: moment.Moment,
): string {
    const kaLocale = RequestInfo.kaLocale;

    const dateOptions = {
        month: "short",
        day: "numeric",
        year: "numeric",
    } as const;

    return `${fromDate
        .toDate()
        .toLocaleDateString(
            kaLocale,
            dateOptions,
        )} - ${upToDate.toDate().toLocaleDateString(kaLocale, dateOptions)}`;
}
